<template>
  <div>
    <div class="error-msg-area">
      <alert-msg v-if="customError" :error-msg="customErrorMsg" component="passcode" />
    </div>

    <div v-if="!connected" class="custom-fields">
      <div
        class="input-container"
        :class="{
          'input-error': ($v.passcode.$error || invalidPasscode) && !isFieldFocused('passcode'),
        }"
      >
        <input
          v-model.trim="passcode"
          class="custom-input"
          type="text"
          placeholder=" "
          @input="handlePasscodeInput"
          @focus="handleFocus('passcode')"
          @blur="handleBlur('passcode')"
        />
        <label class="custom-label">
          <span class="label-text">{{ $t('placeholder_passcode') }}</span>
        </label>
      </div>
      <div
        v-if="($v.passcode.$error || invalidPasscode) && !isFieldFocused('passcode')"
        class="error-message"
      >
        <template v-if="invalidPasscode">
          {{ invalidPasscodeMSG }}
        </template>
        <template v-else>
          {{ $t('passcode_required') }}
        </template>
      </div>
      <div style="position: relative">
        <button
          class="social-btn connect"
          :style="primaryButtonStyles"
          :disabled="loading"
          @click.prevent="connect"
        >
          <template v-if="!loading">
            <i
              v-if="baseData.flags.primaryBtnIcon || false"
              class="fa fa-wifi"
              aria-hidden="true"
            />
            <span>{{ $t('connect_button') }}</span>
          </template>
          <div v-else class="sk-three-bounce">
            <div class="sk-child sk-bounce1" />
            <div class="sk-child sk-bounce2" />
            <div class="sk-child sk-bounce3" />
          </div>
        </button>
      </div>
      <a v-if="baseData.backButton" href="#" @click.prevent="goBack()">← {{ $t('back_button') }}</a>
    </div>

    <div class="wifi-loader-area" style="text-align: center">
      <transition name="slide-fade">
        <div v-if="loading && connected" class="loader">
          <wifi-loader />
          <p>{{ $t('connecting') }}</p>
        </div>
      </transition>
    </div>

    <div style="text-align: center">
      <transition name="slide-fade">
        <div v-if="connected" class="loader">
          <p>{{ $t('operation_success') }}</p>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
  import { required } from 'vuelidate/lib/validators'
  import WifiLoader from '../../components/loader/wifi-loading.vue'
  import AlertMsg from '../../components/errors/alert.vue'
  import { logUserAction } from '../../utils/logging'
  const touchMap = new WeakMap()
  export default {
    components: {
      WifiLoader,
      AlertMsg,
    },

    props: ['api', 'wftkn', 'action'],
    data() {
      return {
        passcode: '',
        loading: false,
        connected: false,
        customError: false,
        customErrorMsg: '',
        validating: false,
        focusedFields: {},
        invalidPasscode: false,
        invalidPasscodeMSG: '',
      }
    },

    validations: {
      passcode: {
        required,
      },
    },

    computed: {
      allValidated() {
        return !this.$v.$invalid
      },
      baseData() {
        return this.$store.state.baseData
      },
      primaryButtonStyles() {
        return {
          'border-radius': `${this.radius}px` || '32px',
          background: this.primaryButtonColor || '#55acee',
          color: this.primaryButtonTextColor || 'white',
          'justify-content': this.primaryButtonTextAlignment || 'center',
        }
      },
      radius() {
        return this.baseData.sizes.loginBtnsRadius
      },
      primaryButtonColor() {
        return this.baseData.colors.primaryBtn
      },
      primaryButtonTextColor() {
        return this.baseData.colors.primaryBtnText
      },
      primaryButtonTextAlignment() {
        return this.baseData.fonts.alignments.primaryBtn
      },
    },

    methods: {
      goBack() {
        logUserAction({ type: 'click', label: 'Passcode Go Back Link' })
        this.$eventHub.$emit('showPasscode', false)
      },
      delayTouch($v) {
        $v.$reset()
        if (touchMap.has($v)) {
          clearTimeout(touchMap.get($v))
        }
        touchMap.set($v, setTimeout($v.$touch, 1000))
      },
      connect() {
        logUserAction({ type: 'user_action', label: 'User Clicked Passcode Connect Button' })
        if (this.api.includes('preview')) {
          return
        }
        this.loading = true
        this.$v.passcode.$touch()
        if (this.$v.passcode.$error) {
          this.loading = false
          return
        }
        this.connectNow()
      },

      allValid() {
        this.delayTouch(this.$v.passcode)
        setTimeout(() => {
          this.validating = false
          if (this.$v.$error) {
            this.loading = false
          }
        }, 1000)
        return !this.$v.$error && this.$v.$dirty
      },

      handlePasscodeInput() {
        this.delayTouch(this.$v.passcode)
        this.invalidPasscode = false
        this.invalidPasscodeMSG = ''
      },

      handlePasscodeError() {
        this.invalidPasscode = true
        this.invalidPasscodeMSG = this.$t('passcode_error')
        this.loading = false
      },

      async connectNow() {
        this.customError = false
        this.customErrorMsg = ''
        this.connected = false
        try {
          const response = await axios.post(
            this.api + '&action=' + this.action,
            qs.stringify({ wftkn: this.wftkn, passcode: this.passcode }),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
          )
          if (!response.data.status) {
            if (response.data.message === 'passcode_error') {
              this.handlePasscodeError()
              return
            } else {
              this.customError = true
              this.customErrorMsg = response.data.message || this.$t('something_went_wrong')
            }
            this.connected = false
          } else {
            this.connected = true
            setTimeout(() => {
              window.location = decodeURIComponent(response.data.url)
            }, 1000)
          }
        } catch (error) {
          const errorMessage = error.response?.data?.message
          if (errorMessage === 'passcode_error') {
            this.handlePasscodeError()
            return
          } else {
            this.customError = true
            this.customErrorMsg = errorMessage || this.$t('something_went_wrong')
          }
          this.connected = false
        } finally {
          this.loading = false
        }
      },
      handleBlur(field) {
        this.focusedFields[field] = false
      },
      handleFocus(fieldName) {
        this.$set(this.focusedFields, fieldName, true)
        this.focusedField = fieldName
      },
      isFieldFocused(field) {
        return this.focusedFields[field] || false
      },
    },
  }
</script>

<style>
  .wifi-loader-area .wifi-symbol .wifi-circle {
    border-color: #55acee;
  }
  .sms-sent-holder {
    width: 100%;
    position: relative;
    display: flex;
    justify-content: center;
  }
  .sms-sent {
    background: #deffd4;
    padding: 10px;
    max-width: 270px;
    border-radius: 5px;
    margin: 0 auto;
    color: #2fbb00;
    text-align: center;
    position: absolute;
    z-index: 1;
    top: 17px;
  }
  .code-buttons {
    display: flex;
    max-width: 317px;
  }
  .code-buttons button:first-child {
    margin-right: 5px;
  }
  .code-buttons button:last-child {
    margin-left: 5px;
    margin-bottom: 10px;
  }
  .connect.sms {
    background: #35c300;
  }
  .connect.sms:hover {
    background: #217500;
  }
  .phone-code {
    display: flex;
    max-width: 320px;
  }
  .phone-code .country-code {
    width: 30px;
    margin-right: 10px;
  }
  .social-btn {
    display: flex !important;
  }
  .social-btn:hover {
    cursor: pointer;
  }
</style>
<style scoped>
  .input-container {
    position: relative;
    width: 320px;
  }
  .label-text {
    transition: 0.2s ease all;
  }
  .input-error {
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
  }

  .input-error input {
    border-color: red !important;
    background: #f7eaea;
  }

  .error-message {
    color: #ff4444;
    font-size: 11px;
    margin-top: 2px;
    text-align: left;
    padding-left: 45px;
    width: 100%;
    width: 320px;
  }

  .social-btn {
    position: relative;
    transition: all 0.3s ease;
  }

  .social-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
</style>
